import { useMutation, UseMutationResult } from 'react-query';
import createAuthenticatedFetch from './createAuthenticatedFetch.ts';
import useBotPagesConfig from '../hooks/useBotPagesConfig.ts';
import { useMsal } from '@azure/msal-react';
import handleResponseError from './handleResponseError.ts';
import usePagesUserId from '../hooks/usePagesUserId.ts';
import apiPath from './apiPath.ts';

interface SendMessageRequest {
    threadId: string;
    message: string;
}

const useSendMessage = (
    onLineReceived: (line: string) => void
): UseMutationResult<void, Error, SendMessageRequest> => {
    const { clientId, requiresAuthentication } = useBotPagesConfig();
    const msalInfo = useMsal();
    const pagesUserId = usePagesUserId();
    const authenticatedFetch = createAuthenticatedFetch(
        clientId,
        msalInfo,
        requiresAuthentication
    );
    return useMutation<void, Error, SendMessageRequest>(async (request) => {
        const response = await authenticatedFetch(
            apiPath('/api/knowledge-assistant/threads/send-message'),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Pages-User-Id': pagesUserId
                },
                body: JSON.stringify(request)
            }
        );

        if (!response.ok) {
            await handleResponseError(response);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (buffer.length > 0) {
                    onLineReceived(buffer.replace(/\r$/, ''));
                }
                break;
            }

            buffer += decoder.decode(value, { stream: true });

            let newlineIndex: number;
            while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
                const line = buffer.slice(0, newlineIndex).replace(/\r$/, '');
                buffer = buffer.slice(newlineIndex + 1);
                onLineReceived(line);
            }
        }
    });
};

export default useSendMessage;
